import { CartUpdateEvent } from '@theme/events';

const BLOCK_SELECTOR = '[data-vairay-sts-v2-block]';
const SERIES_SLOT_KEYS = {
  islands: [{ slot: 1, key: 'islands_main_1' }, { slot: 2, key: 'islands_main_2' }, { slot: 3, key: 'accent_variant' }, { slot: 4, key: 'neck_variant' }, { slot: 5, key: 'pocket_variant' }],
  oceans: [{ slot: 1, key: 'oceans_frame' }, { slot: 2, key: 'oceans_inlay' }, { slot: 3, key: 'accent_variant' }, { slot: 4, key: 'neck_variant' }, { slot: 5, key: 'pocket_variant' }],
  metropolis: [{ slot: 1, key: 'metropolis_main' }, { slot: 3, key: 'accent_variant' }, { slot: 4, key: 'neck_variant' }, { slot: 5, key: 'pocket_variant' }],
};

const SLOT_PART_LABELS = {
  islands_main_1: 'Islands 1',
  islands_main_2: 'Islands 2',
  oceans_frame: 'Reef Frame',
  oceans_inlay: 'Inlay',
  metropolis_main: 'Metropolis',
  accent_variant: 'Akzentband',
  neck_variant: 'Nackenband',
  pocket_variant: 'Einstecktuch',
};

const slugifySetName = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const createSetId = (setName) => {
  const slug = slugifySetName(setName);
  return `STS-${slug || 'shop-the-style'}`;
};

const getVariantLabel = (slot) => {
  if (!slot?.variant_title || slot.variant_title === 'Default Title') return slot?.product_title || '';
  return slot.variant_title;
};

const formatMoney = (cents) => {
  const value = Number(cents || 0) / 100;
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
};

const parseSlotsFromConfiguratorUrl = (urlValue) => {
  if (!urlValue) return [];
  let url;
  try { url = new URL(urlValue, window.location.origin); } catch (_) { return []; }
  const params = new URLSearchParams(url.search);
  const series = (params.get('series') || '').trim().toLowerCase();
  const keyMap = SERIES_SLOT_KEYS[series];
  if (!keyMap) return [];
  return keyMap.map(({ slot, key }) => ({ slot, key, variantId: Number(params.get(key) || 0) })).filter((entry) => Number.isFinite(entry.variantId) && entry.variantId > 0);
};

const fetchVariantSlot = async ({ slot, key, variantId }) => {
  const response = await fetch(`/variants/${variantId}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Variante ${variantId} konnte nicht geladen werden.`);
  const variant = await response.json();
  return {
    slot,
    key,
    part_name: SLOT_PART_LABELS[key] || '',
    product_handle: variant.handle || '',
    product_title: variant.product_title || '',
    variant_id: Number(variant.id || 0),
    variant_title: variant.public_title || variant.title || '',
    price: Number(variant.price || 0),
    available: Boolean(variant.available),
    inventory_quantity: Number(variant.inventory_quantity ?? 1),
    missing: false,
  };
};

const getSectionsToRender = () => {
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  });
  return Array.from(sectionIds);
};

const fetchSectionsHtml = async (sectionIds) => {
  if (!sectionIds.length) return {};
  const shopRoot = window?.Shopify?.routes?.root || '/';
  const response = await fetch(`${shopRoot}?sections=${encodeURIComponent(sectionIds.join(','))}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
  return response.json();
};

const dispatchCartUpdate = async () => {
  const [cartResponse, sections] = await Promise.all([
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json()),
    fetchSectionsHtml(getSectionsToRender()),
  ]);

  document.dispatchEvent(new CartUpdateEvent(cartResponse, 'vairay-sts-v2', {
    source: 'vairay-sts-v2',
    itemCount: Number(cartResponse?.item_count || 0),
    sections,
  }));

  const cartDrawer = document.querySelector('cart-drawer-component');
  if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();
};

const showFeedback = (node, message = '', isError = false) => {
  if (!node) return;
  node.textContent = message;
  node.classList.toggle('is-error', isError);
};

const initBlock = (block) => {
  if (!(block instanceof HTMLElement) || block.dataset.vairayStsV2Initialized === 'true') return;

  const media = block.querySelector('.vairay-sts-v2-block__media');
  const openBtn = block.querySelector('[data-vairay-sts-v2-open]');
  const dialog = block.querySelector('[data-vairay-sts-v2-dialog]');
  const closeBtn = block.querySelector('[data-vairay-sts-v2-close]');
  const partList = block.querySelector('[data-vairay-sts-v2-parts]');
  const totalNode = block.querySelector('[data-vairay-sts-v2-total]');
  const totalRow = block.querySelector('[data-vairay-sts-v2-total-row]');
  const configLink = block.querySelector('[data-vairay-sts-v2-config-link]');
  const addAllButton = block.querySelector('[data-vairay-sts-v2-add-all]');
  const availabilityNote = block.querySelector('[data-vairay-sts-v2-availability-note]');
  const feedback = block.querySelector('[data-vairay-sts-v2-feedback]');

  if (!openBtn || !dialog || !closeBtn || !partList || !totalNode || !configLink || !addAllButton) return;
  const configUrl = configLink.dataset.configuratorUrl || configLink.getAttribute('href') || '';
  let configuredSlots = [];
  let validSlots = [];
  let hasUnavailableParts = false;
  let availableSlots = [];
  const isSlotUnavailable = (slot) => Number(slot?.variant_id) <= 0
    || slot.available === false
    || slot.missing === true
    || Number(slot?.inventory_quantity) <= 0;

  const renderSlots = () => {
    hasUnavailableParts = configuredSlots.some((slot) => isSlotUnavailable(slot));
    availableSlots = validSlots.filter((slot) => !isSlotUnavailable(slot));

    const createPartNameNode = (partName, productTitle) => {
    const productName = document.createElement('span');
    productName.className = 'vairay-sts-v2-block__part-name';
    productName.textContent = partName || productTitle;
    return productName;
  };

  const createPartLine = ({ partName, productTitle, variantLabel, isUnavailable, price }) => {
    const fragment = document.createDocumentFragment();
    fragment.appendChild(createPartNameNode(partName, productTitle));
    fragment.appendChild(document.createTextNode(` - ${variantLabel} - `));

    if (isUnavailable) {
      const soldOut = document.createElement('span');
      soldOut.className = 'vairay-sts-v2-block__part-sold-out';
      soldOut.textContent = 'leider ausverkauft';
      fragment.appendChild(soldOut);
      return fragment;
    }

    fragment.appendChild(document.createTextNode(formatMoney(price)));
    return fragment;
  };

    partList.innerHTML = '';
    configuredSlots.forEach((slot) => {
    const li = document.createElement('li');
    li.className = 'vairay-sts-v2-block__part';

    const isUnavailable = isSlotUnavailable(slot);
    if (isUnavailable) li.classList.add('vairay-sts-v2-block__part--unavailable');

    li.appendChild(createPartLine({
      partName: slot.part_name,
      productTitle: slot.product_title,
      variantLabel: getVariantLabel(slot),
      isUnavailable,
      price: slot.price,
    }));

    partList.appendChild(li);
    });

    const total = availableSlots.reduce((sum, slot) => sum + Number(slot.price || 0), 0);
    totalNode.textContent = formatMoney(total);
    if (totalRow) totalRow.hidden = hasUnavailableParts;
  };

  const soldOutLabel = addAllButton.dataset.soldOutLabel || 'Leider ausverkauft';
  const addAllLabel = addAllButton.querySelector('.add-to-cart-text__content > span > span');
  const defaultLabel = addAllButton.dataset.defaultLabel || addAllLabel?.textContent?.trim() || 'In den Warenkorb';

  const syncAvailabilityState = () => {
    if (hasUnavailableParts || !validSlots.length) {
      if (addAllLabel) addAllLabel.textContent = soldOutLabel;
      addAllButton.setAttribute('disabled', 'disabled');
      addAllButton.classList.add('vairay-sts-v2-block__action--disabled');
      if (availabilityNote) availabilityNote.hidden = false;
      return;
    }

    if (addAllLabel) addAllLabel.textContent = defaultLabel;
    addAllButton.removeAttribute('disabled');
    addAllButton.classList.remove('vairay-sts-v2-block__action--disabled');
    if (availabilityNote) availabilityNote.hidden = true;
  };

  const loadSlots = async () => {
    const slotRefs = parseSlotsFromConfiguratorUrl(configUrl);
    if (!slotRefs.length) {
      configuredSlots = [];
      validSlots = [];
      renderSlots();
      syncAvailabilityState();
      return;
    }
    try {
      configuredSlots = await Promise.all(slotRefs.map((entry) => fetchVariantSlot(entry)));
      validSlots = configuredSlots.filter((slot) => Number(slot?.variant_id) > 0);
      renderSlots();
      syncAvailabilityState();
    } catch (error) {
      configuredSlots = [];
      validSlots = [];
      renderSlots();
      syncAvailabilityState();
      showFeedback(feedback, error.message || 'Konfigurator-Daten konnten nicht geladen werden.', true);
    }
  };

  const openConfigurator = () => {
    window.location.href = configUrl || '#';
  };

  const releaseZoomLock = () => {
    block.classList.remove('vairay-sts-v2-block--suspend-hover');
  };

  const resetImageZoomState = () => {
    block.classList.add('vairay-sts-v2-block--suspend-hover');
    openBtn.blur();

    if (!(media instanceof HTMLElement) || !media.matches(':hover')) {
      releaseZoomLock();
    }
  };

  openBtn.addEventListener('click', () => {
    releaseZoomLock();
    if (!configuredSlots.length) loadSlots();
    syncAvailabilityState();
    dialog.showModal();
  });
  closeBtn.addEventListener('click', () => dialog.close());
  dialog.addEventListener('close', resetImageZoomState);

  media?.addEventListener('pointerleave', releaseZoomLock);

  dialog.addEventListener('click', (event) => {
    const rect = dialog.getBoundingClientRect();
    const isBackdrop = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    if (isBackdrop) dialog.close();
  });

  configLink.addEventListener('click', (event) => {
    event.preventDefault();
    openConfigurator();
  });

  addAllButton.addEventListener('click', async () => {
    if (hasUnavailableParts) return;
    if (!validSlots.length) {
      showFeedback(feedback, 'Keine Produkte/Varianten konfiguriert.', true);
      return;
    }

    const setId = createSetId();
    const setName = configLink.dataset.setName || 'Shop the Style';
    const items = validSlots.map((slot) => ({
      id: Number(slot.variant_id),
      quantity: 1,
      properties: { Set: setName, SetID: setId, _Part: `p${Number(slot?.slot)}` },
    }));

    addAllButton.setAttribute('disabled', 'disabled');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload?.description || `Fehler beim Hinzufügen (${response.status})`);
      }

      await dispatchCartUpdate();
      showFeedback(feedback);
      dialog.close();
    } catch (error) {
      showFeedback(feedback, error.message || 'Konnte Produkte nicht hinzufügen.', true);
    } finally {
      addAllButton.removeAttribute('disabled');
      if (hasUnavailableParts) addAllButton.setAttribute('disabled', 'disabled');
    }
  });

  block.dataset.vairayStsV2Initialized = 'true';
  loadSlots();
};

const init = () => document.querySelectorAll(BLOCK_SELECTOR).forEach((block) => initBlock(block));

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

document.addEventListener('shopify:section:load', init);
